import {
  useAnimations,
  useGLTF,
  Trail,
  SpriteAnimator,
  useSpriteLoader,
} from "@react-three/drei";
import { useControls } from "leva";
import { Suspense, useEffect, useRef, useMemo, useState, useCallback } from "react";
import * as THREE from "three";
import { useGame } from "../src/stores/useGame";
import { BallCollider, vec3 } from "@react-three/rapier";
import { useFrame, useGraph, useThree, createPortal } from "@react-three/fiber";
import { SkeletonUtils } from "three-stdlib";
import { useInteractionStore } from "../src/stores/useInteraction";
import { usePlayerHealth, DAMAGE_FLASH_DURATION_MS } from "../src/stores/usePlayerHealth";
import { useInventory } from "../src/stores/useInventory";
import { emitEvent } from "../src/events/eventBus";

const DANCE_FLOOR_ACTION = "DanceFloorLoop";
const DANCE_FLOOR_SOURCE_INDEX = 9; // animation #8 (1-based)
const DAMAGE_ANIMATION_INDEX = 14; // animation #13 (1-based)
const GUN_FIRE_RATE = 12;
const GUN_FIRE_INTERVAL_MS = 1000 / GUN_FIRE_RATE;
const GUN_BULLET_SPEED = 90;
const GUN_BULLET_RANGE = 50;
const GUN_BULLET_CAPACITY = 64;
const MUZZLE_FLASH_DURATION_MS = 60;
const MUZZLE_FLASH_OFFSET = new THREE.Vector3(1.2, -.05, .25);
const GUN_BULLETS_PER_BURST = 4;
const GUN_BULLET_SPREAD = 0.16;
const GUN_BULLET_JITTER = 0.04;
const BULLET_RADIUS = 0.07;
const BULLET_LENGTH = 0.9;
const BULLET_COLOR = "#fff7ba";
const BULLET_EMISSIVE = "#ffae00";
const BULLET_EMISSIVE_INTENSITY = 4.2;

export default function CharacterModel(props) {
  // Change the character src to yours
  const group = useRef();
  const { scene: gltfScene, animations } = useGLTF("/personaje.glb");
  const clonedScene = useMemo(() => SkeletonUtils.clone(gltfScene), [gltfScene]);
  const extendedAnimations = useMemo(() => {
    if (!animations || animations.length <= DANCE_FLOOR_SOURCE_INDEX) {
      return animations ?? [];
    }

    const clonedClip = animations[DANCE_FLOOR_SOURCE_INDEX].clone();
    clonedClip.name = DANCE_FLOOR_ACTION;
    return [...animations, clonedClip];
  }, [animations]);
  const damageClipName = useMemo(() => {
    return extendedAnimations?.[DAMAGE_ANIMATION_INDEX]?.name ?? null;
  }, [extendedAnimations]);
  const { nodes, materials } = useGraph(clonedScene);
  const { actions } = useAnimations(extendedAnimations, group);
  const trailRef = useRef();
  const targetOpacity = useInteractionStore((state) => state.playerOpacityTarget);
  const currentOpacityRef = useRef(targetOpacity);
  const fadeDuration = 0.15;
  const miniGunRef = useRef(null);
  const hasHeavyMachineGun = useInventory((state) => state.hasHeavyMachineGun);
  const gunMode = useInventory((state) => state.gunMode);
  const setIsShooting = useInventory((state) => state.setIsShooting);
  const previousHeavyMachineGunRef = useRef(hasHeavyMachineGun);
  const pickupAnimationNames = useMemo(
    () => ({
      primary: extendedAnimations?.[15]?.name ?? null, // animation index 16 (1-based)
      secondary: extendedAnimations?.[40]?.name ?? null, // animation index 41 (1-based)
      tertiary: extendedAnimations?.[45]?.name ?? null, // animation index 46 (1-based)
    }),
    [extendedAnimations]
  );
  const gunIdleClipName = useMemo(() => extendedAnimations?.[17]?.name ?? null, [extendedAnimations]);
  const gunWalkClipName = useMemo(() => extendedAnimations?.[18]?.name ?? null, [extendedAnimations]);
  const shootingStartClipName = useMemo(() => extendedAnimations?.[45]?.name ?? null, [extendedAnimations]);
  const shootingLoopPrimaryClipName = useMemo(() => extendedAnimations?.[44]?.name ?? null, [extendedAnimations]);
  const shootingLoopSecondaryClipName = useMemo(() => extendedAnimations?.[16]?.name ?? null, [extendedAnimations]);
  const camera = useThree((state) => state.camera);
  const orientationStateRef = useRef({
    phase: "idle",
    startTime: 0,
    duration: 0.2,
    initialQuat: new THREE.Quaternion(),
    targetQuat: new THREE.Quaternion(),
    originalQuat: new THREE.Quaternion(),
    onComplete: null,
    pendingReturn: false,
  });
  const orientationTempQuat = useMemo(() => new THREE.Quaternion(), []);
  const orientationWorldPos = useMemo(() => new THREE.Vector3(), []);
  const orientationTargetDir = useMemo(() => new THREE.Vector3(), []);
  const orientationCurrentForward = useMemo(() => new THREE.Vector3(), []);
  const orientationUp = useMemo(() => new THREE.Vector3(0, 1, 0), []);
  const orientationYawQuat = useMemo(() => new THREE.Quaternion(), []);
  const pickupMixerCleanupRef = useRef([]);
  const pickupFallbackTimeoutRef = useRef(null);
  const pickupActiveActionsRef = useRef(new Set());
  const gunWalkStopTimeoutRef = useRef(null);
  const shootingStateRef = useRef("idle");
  const shootingPointerHeldRef = useRef(false);
  const shootingLoopActionsRef = useRef([]);
  const shootingStartActionRef = useRef(null);
  const shootingStopTimeoutsRef = useRef([]);
  const shootingStartStopTimeoutRef = useRef(null);
  const shootingIdleActionRef = useRef(null);
  const shootingIdleStopTimeoutRef = useRef(null);
  const gunTransitionFade = 0.18;
  const r3fScene = useThree((state) => state.scene);
  const muzzleRef = useRef(null);
  const muzzleFlashRef = useRef(null);
  const muzzleFlashMaterialRef = useRef(null);
  const muzzleFlashTimeoutRef = useRef(null);
  const gunfireIntervalRef = useRef(null);
  const bulletInstancedMeshRef = useRef(null);
  const bulletIdRef = useRef(0);
  const bulletPoolRef = useRef(
    Array.from({ length: GUN_BULLET_CAPACITY }, (_, index) => ({
      index,
      active: false,
      distance: 0,
      position: new THREE.Vector3(),
      direction: new THREE.Vector3(),
      quaternion: new THREE.Quaternion(),
    }))
  );
  const bulletTempMatrix = useMemo(() => new THREE.Matrix4(), []);
  const hiddenMatrix = useMemo(() => new THREE.Matrix4().makeScale(0, 0, 0), []);
  const bulletScale = useMemo(() => new THREE.Vector3(1.1, 1, 1.1), []);
  const muzzleWorldPos = useMemo(() => new THREE.Vector3(), []);
  const muzzleWorldQuat = useMemo(() => new THREE.Quaternion(), []);
  const upVector = useMemo(() => new THREE.Vector3(0, 1, 0), []);
  const muzzleFlashOffsetWorld = useMemo(() => new THREE.Vector3(), []);
  const spawnOriginWorld = useMemo(() => new THREE.Vector3(), []);
  const playerForwardDirection = useMemo(() => new THREE.Vector3(), []);
  const bulletRightVector = useMemo(() => new THREE.Vector3(), []);

  useEffect(() => {
    const instanced = bulletInstancedMeshRef.current;
    if (!instanced) return;
    instanced.instanceMatrix.setUsage?.(THREE.DynamicDrawUsage);
    bulletPoolRef.current.forEach((bullet) => {
      instanced.setMatrixAt(bullet.index, hiddenMatrix);
    });
    instanced.instanceMatrix.needsUpdate = true;
    instanced.visible = false;
  }, [hiddenMatrix]);

  const hideMuzzleFlash = useCallback(() => {
    if (muzzleFlashTimeoutRef.current !== null && typeof window !== "undefined") {
      window.clearTimeout(muzzleFlashTimeoutRef.current);
      muzzleFlashTimeoutRef.current = null;
    }
    const flash = muzzleFlashRef.current;
    if (!flash) return;
    flash.visible = false;
    const material = muzzleFlashMaterialRef.current;
    if (material) {
      material.opacity = 0;
    }
  }, []);

  const showMuzzleFlash = useCallback(
    (position, quaternion) => {
      const flash = muzzleFlashRef.current;
      const material = muzzleFlashMaterialRef.current;
      if (!flash || !material) return;

  muzzleFlashOffsetWorld.copy(MUZZLE_FLASH_OFFSET).applyQuaternion(quaternion);
  flash.position.copy(position).add(muzzleFlashOffsetWorld);
      flash.quaternion.copy(quaternion);
      flash.scale.setScalar(0.45 + Math.random() * 0.25);
      flash.visible = true;
      material.opacity = 0.95;

      if (muzzleFlashTimeoutRef.current !== null && typeof window !== "undefined") {
        window.clearTimeout(muzzleFlashTimeoutRef.current);
      }

      if (typeof window !== "undefined") {
        muzzleFlashTimeoutRef.current = window.setTimeout(() => {
          hideMuzzleFlash();
        }, MUZZLE_FLASH_DURATION_MS);
      }
    },
    [hideMuzzleFlash, muzzleFlashOffsetWorld]
  );

  const spawnBulletBurst = useCallback(() => {
    if (!gunMode || !hasHeavyMachineGun) return;
    const instanced = bulletInstancedMeshRef.current;
    const muzzleNode = muzzleRef.current || miniGunRef.current;
    const characterGroup = group.current;
    if (!instanced || !muzzleNode || !characterGroup) return;

    muzzleNode.getWorldPosition(muzzleWorldPos);
    muzzleNode.getWorldQuaternion(muzzleWorldQuat);
    muzzleFlashOffsetWorld.copy(MUZZLE_FLASH_OFFSET).applyQuaternion(muzzleWorldQuat);
    spawnOriginWorld.copy(muzzleWorldPos).add(muzzleFlashOffsetWorld);

    characterGroup.getWorldDirection(playerForwardDirection);
    playerForwardDirection.y = 0;
    if (playerForwardDirection.lengthSq() < 1e-6) {
      playerForwardDirection.set(0, 0, -1).applyQuaternion(characterGroup.quaternion);
      playerForwardDirection.y = 0;
    }
    if (playerForwardDirection.lengthSq() < 1e-6) {
      playerForwardDirection.set(0, 0, -1);
    }
    playerForwardDirection.normalize();

    bulletRightVector.copy(playerForwardDirection).cross(upVector);
    if (bulletRightVector.lengthSq() < 1e-6) {
      bulletRightVector.set(1, 0, 0);
    } else {
      bulletRightVector.normalize();
    }

    const bulletPool = bulletPoolRef.current;
    let anySpawned = false;

    for (let i = 0; i < GUN_BULLETS_PER_BURST; i++) {
      const entry = bulletPool.find((bullet) => !bullet.active);
      if (!entry) break;

      const spreadIndex = i - (GUN_BULLETS_PER_BURST - 1) / 2;
      const lateralOffset = spreadIndex * GUN_BULLET_SPREAD;
      const directionJitter = (Math.random() - 0.5) * GUN_BULLET_JITTER;

      entry.position.copy(spawnOriginWorld).addScaledVector(bulletRightVector, lateralOffset);
      entry.direction
        .copy(playerForwardDirection)
        .addScaledVector(bulletRightVector, directionJitter)
        .normalize();
      entry.direction.y = 0;
      entry.direction.normalize();
      entry.distance = 0;
      entry.active = true;
      entry.quaternion.setFromUnitVectors(upVector, entry.direction);

      bulletTempMatrix.compose(entry.position, entry.quaternion, bulletScale);
      instanced.setMatrixAt(entry.index, bulletTempMatrix);
      instanced.instanceMatrix.needsUpdate = true;
      instanced.visible = true;

      const bulletId = bulletIdRef.current++;
      emitEvent("bullet-fired", {
        id: bulletId,
        burstIndex: i,
        origin: [entry.position.x, entry.position.y, entry.position.z],
        direction: [entry.direction.x, entry.direction.y, entry.direction.z],
        speed: GUN_BULLET_SPEED,
        range: GUN_BULLET_RANGE,
        timestamp: typeof performance !== "undefined" ? performance.now() : Date.now(),
      });

      anySpawned = true;
    }

    if (anySpawned) {
      showMuzzleFlash(muzzleWorldPos, muzzleWorldQuat);
    }
  }, [
    bulletRightVector,
    bulletScale,
    bulletTempMatrix,
    gunMode,
    hasHeavyMachineGun,
    miniGunRef,
    muzzleFlashOffsetWorld,
    muzzleWorldPos,
    muzzleWorldQuat,
    playerForwardDirection,
    showMuzzleFlash,
    spawnOriginWorld,
    upVector,
  ]);

  const startGunfireEffects = useCallback(() => {
    if (!gunMode || !hasHeavyMachineGun) return;

    spawnBulletBurst();
    if (gunfireIntervalRef.current !== null || typeof window === "undefined") {
      return;
    }

    gunfireIntervalRef.current = window.setInterval(() => {
      spawnBulletBurst();
    }, GUN_FIRE_INTERVAL_MS);
  }, [gunMode, hasHeavyMachineGun, spawnBulletBurst]);

  const stopGunfireEffects = useCallback(
    (immediate = false) => {
      if (gunfireIntervalRef.current !== null && typeof window !== "undefined") {
        window.clearInterval(gunfireIntervalRef.current);
        gunfireIntervalRef.current = null;
      }

      if (immediate) {
        const instanced = bulletInstancedMeshRef.current;
        if (instanced) {
          let needsUpdate = false;
          bulletPoolRef.current.forEach((bullet) => {
            if (!bullet.active) return;
            bullet.active = false;
            bullet.distance = 0;
            instanced.setMatrixAt(bullet.index, hiddenMatrix);
            needsUpdate = true;
          });
          if (needsUpdate) {
            instanced.instanceMatrix.needsUpdate = true;
          }
          instanced.visible = false;
        }
      }

      hideMuzzleFlash();
    },
    [hideMuzzleFlash, hiddenMatrix]
  );

  const restoreOrientation = useCallback(() => {
    if (!group.current) return;
    const orientationState = orientationStateRef.current;
    group.current.quaternion.copy(orientationState.originalQuat);
    orientationState.phase = "idle";
    orientationState.pendingReturn = false;
    orientationState.onComplete = null;
    orientationState.initialQuat.copy(orientationState.originalQuat);
    orientationState.targetQuat.copy(orientationState.originalQuat);
  }, []);

  const stopShootingLoop = useCallback(
    (immediate = false) => {
      stopGunfireEffects(immediate);

      if (!shootingLoopActionsRef.current.length) {
        shootingStopTimeoutsRef.current.forEach((timeoutId) => clearTimeout(timeoutId));
        shootingStopTimeoutsRef.current = [];
        return;
      }

      if (immediate) {
        shootingLoopActionsRef.current.forEach((loopAction) => {
          loopAction?.stop?.();
        });
        shootingLoopActionsRef.current = [];
        shootingStopTimeoutsRef.current.forEach((timeoutId) => clearTimeout(timeoutId));
        shootingStopTimeoutsRef.current = [];
        return;
      }

      shootingLoopActionsRef.current.forEach((loopAction) => {
        if (!loopAction) return;
        const fade = gunTransitionFade;
        if (fade > 0) {
          loopAction.fadeOut?.(fade);
          if (typeof window !== "undefined") {
            const timeoutId = window.setTimeout(() => {
              loopAction.stop?.();
              shootingStopTimeoutsRef.current = shootingStopTimeoutsRef.current.filter(
                (storedId) => storedId !== timeoutId
              );
            }, fade * 1000);
            shootingStopTimeoutsRef.current.push(timeoutId);
          }
        } else {
          loopAction.stop?.();
        }
      });

      shootingLoopActionsRef.current = [];
    },
    [gunTransitionFade, stopGunfireEffects]
  );

  const stopShootingStart = useCallback(
    (immediate = false) => {
      const data = shootingStartActionRef.current;
      if (!data) return;

      const { action, mixer, handler } = data;
      if (mixer && handler) {
        mixer.removeEventListener("finished", handler);
      }

      if (immediate) {
        if (shootingStartStopTimeoutRef.current !== null) {
          clearTimeout(shootingStartStopTimeoutRef.current);
          shootingStartStopTimeoutRef.current = null;
        }
        action.stop?.();
      } else {
        const fade = gunTransitionFade;
        if (fade > 0) {
          action.fadeOut?.(fade);
          if (typeof window !== "undefined") {
            if (shootingStartStopTimeoutRef.current !== null) {
              clearTimeout(shootingStartStopTimeoutRef.current);
              shootingStartStopTimeoutRef.current = null;
            }
            shootingStartStopTimeoutRef.current = window.setTimeout(() => {
              action.stop?.();
              shootingStartStopTimeoutRef.current = null;
            }, fade * 1000);
          }
        } else {
          action.stop?.();
        }
      }

      shootingStartActionRef.current = null;
    },
    [gunTransitionFade]
  );

  const stopShootingIdleAction = useCallback(
    (immediate = false) => {
      const idleAction = shootingIdleActionRef.current;
      if (!idleAction) return;

      if (shootingIdleStopTimeoutRef.current !== null) {
        clearTimeout(shootingIdleStopTimeoutRef.current);
        shootingIdleStopTimeoutRef.current = null;
      }

      if (gunMode && !immediate) {
        // Keep the gun idle clip alive so we don't drop to a T-pose when releasing the trigger.
        const isRunning = typeof idleAction.isRunning === "function" ? idleAction.isRunning() : idleAction.isRunning;
        if (!isRunning) {
          idleAction.reset().fadeIn?.(gunTransitionFade).setLoop(THREE.LoopRepeat, Infinity).play();
        }
        idleAction.enabled = true;
        return;
      }

      if (immediate || gunTransitionFade <= 0) {
        idleAction.stop?.();
        shootingIdleActionRef.current = null;
        return;
      }

      idleAction.fadeOut?.(gunTransitionFade);
      if (typeof window !== "undefined") {
        const timeoutId = window.setTimeout(() => {
          idleAction.stop?.();
          shootingIdleStopTimeoutRef.current = null;
        }, gunTransitionFade * 1000);
        shootingIdleStopTimeoutRef.current = timeoutId;
      } else {
        idleAction.stop?.();
      }
      shootingIdleActionRef.current = null;
    },
    [gunMode, gunTransitionFade]
  );

  const stopShootingSequence = useCallback(
    (immediate = false) => {
      stopShootingLoop(immediate);
      if (shootingStartStopTimeoutRef.current !== null) {
        clearTimeout(shootingStartStopTimeoutRef.current);
        shootingStartStopTimeoutRef.current = null;
      }
      stopShootingStart(immediate);
      stopShootingIdleAction(immediate);
      shootingStateRef.current = "idle";
    },
    [stopShootingIdleAction, stopShootingLoop, stopShootingStart]
  );

  const ensureShootingIdleAction = useCallback(() => {
    if (!actions || !gunIdleClipName) {
      return null;
    }

    const idleAction = actions[gunIdleClipName];
    if (!idleAction) {
      return null;
    }

    if (shootingIdleStopTimeoutRef.current !== null) {
      clearTimeout(shootingIdleStopTimeoutRef.current);
      shootingIdleStopTimeoutRef.current = null;
    }

    const isRunning = typeof idleAction.isRunning === "function" ? idleAction.isRunning() : idleAction.isRunning;
    if (!isRunning) {
      idleAction.reset().fadeIn?.(gunTransitionFade).setLoop(THREE.LoopRepeat, Infinity).play();
      idleAction.clampWhenFinished = false;
    } else {
      idleAction.enabled = true;
    }

    shootingIdleActionRef.current = idleAction;
    return idleAction;
  }, [actions, gunIdleClipName, gunTransitionFade]);

  const startShootingLoop = useCallback(() => {
    if (!actions) return;

    shootingStopTimeoutsRef.current.forEach((timeoutId) => clearTimeout(timeoutId));
    shootingStopTimeoutsRef.current = [];

    const idleAction = ensureShootingIdleAction();
    const loopActions = [];
    [shootingLoopPrimaryClipName, shootingLoopSecondaryClipName].forEach((clipName) => {
      if (!clipName) return;
      if (clipName === gunIdleClipName) {
        return;
      }
      const loopAction = actions[clipName];
      if (!loopAction) return;
      loopAction.reset().fadeIn?.(gunTransitionFade).setLoop(THREE.LoopRepeat, Infinity).play();
      loopAction.clampWhenFinished = false;
      loopActions.push(loopAction);
    });

    shootingLoopActionsRef.current = loopActions;
    const hasLoopingAction = loopActions.length > 0 || Boolean(idleAction);
    shootingStateRef.current = hasLoopingAction ? "looping" : "idle";
    if (hasLoopingAction) {
      startGunfireEffects();
      emitEvent("shooting", { phase: "loop" });
    } else {
      stopGunfireEffects(true);
    }
  }, [
    actions,
    ensureShootingIdleAction,
    gunIdleClipName,
    gunTransitionFade,
    startGunfireEffects,
    stopGunfireEffects,
    shootingLoopPrimaryClipName,
    shootingLoopSecondaryClipName,
  ]);

  const startShootingSequence = useCallback(() => {
    if (!gunMode || !actions) return;
    if (shootingStateRef.current !== "idle") return;

    stopShootingLoop(true);
    if (shootingStartStopTimeoutRef.current !== null) {
      clearTimeout(shootingStartStopTimeoutRef.current);
      shootingStartStopTimeoutRef.current = null;
    }

    const startClipName = shootingStartClipName;
    const startAction = startClipName ? actions[startClipName] : null;
    ensureShootingIdleAction();

    if (!startAction) {
      startShootingLoop();
      return;
    }

    shootingStateRef.current = "starting";

    const mixer = startAction._mixer;
    const handleFinished = (event) => {
      if (event.action !== startAction) return;
      if (mixer) {
        mixer.removeEventListener("finished", handleFinished);
      }
      shootingStartActionRef.current = null;

      if (shootingPointerHeldRef.current && gunMode) {
        startShootingLoop();
      } else {
        shootingStateRef.current = "idle";
      }
    };

    if (mixer) {
      mixer.addEventListener("finished", handleFinished);
      shootingStartActionRef.current = {
        action: startAction,
        mixer,
        handler: handleFinished,
      };
    }

    startAction.reset().fadeIn?.(gunTransitionFade).setLoop(THREE.LoopOnce, 1).play();
    startAction.clampWhenFinished = true;
  }, [
    actions,
    ensureShootingIdleAction,
    gunMode,
    gunTransitionFade,
    shootingStartClipName,
    startShootingLoop,
    stopShootingLoop,
  ]);

  /**
   * Prepare hands ref for attack action
   */
  const rightHandRef = useRef();
  const rightHandColliderRef = useRef();
  const leftHandRef = useRef();
  const leftHandColliderRef = useRef();
  const rightHandPos = useMemo(() => new THREE.Vector3(), []);
  const leftHandPos = useMemo(() => new THREE.Vector3(), []);
  const bodyPos = useMemo(() => new THREE.Vector3(), []);
  const bodyRot = useMemo(() => new THREE.Quaternion(), []);
  let rightHand = null;
  let leftHand = null;

  /**
   * Prepare punch effect sprite
   */
  const { spriteObj } = useSpriteLoader("./punchEffect.png", null, null, 7);
  const [punchEffectProps, setPunchEffectProp] = useState({
    visible: false,
    scale: [1, 1, 1],
    play: false,
    position: [-0.2, -0.2, 0.5],
    startFrame: 0,
  });

  /**
   * Debug settings
   */
  const { trailColor } = useControls("Character Model", {
    trailColor: "orange",
  });

  useEffect(() => {
    if (materials?.outline) {
      materials.outline.skinning = true;
    }
  }, [materials]);

  const bodyMeshes = useMemo(() => {
    if (!nodes) return [];
    return Object.values(nodes)
      .filter((node) => node?.isSkinnedMesh && node.name !== "outline")
      .map((mesh) => {
        if (Array.isArray(mesh.material)) {
          mesh.material.forEach((material) => {
            if (material) material.skinning = true;
          });
        } else if (mesh.material) {
          mesh.material.skinning = true;
        }
        return mesh;
      })
      .sort((a, b) => (a.name || "").localeCompare(b.name || ""));
  }, [nodes]);

  const materialsToFade = useMemo(() => {
    const set = new Set();
    bodyMeshes.forEach((mesh) => {
      if (!mesh) return;
      if (Array.isArray(mesh.material)) {
        mesh.material.forEach((material) => material && set.add(material));
      } else if (mesh.material) {
        set.add(mesh.material);
      }
    });
    if (nodes?.outline?.material) {
      set.add(nodes.outline.material);
    }
    return Array.from(set);
  }, [bodyMeshes, nodes]);

  const materialDefaultsRef = useRef(new Map());
  const damageTintDefaultsRef = useRef(new Map());
  const damageTintTimeoutRef = useRef(null);
  const damageTintColor = useMemo(() => new THREE.Color("#ff4d4f"), []);

  useEffect(() => {
    materialsToFade.forEach((material) => {
      if (!materialDefaultsRef.current.has(material)) {
        materialDefaultsRef.current.set(material, {
          opacity: "opacity" in material ? material.opacity : 1,
          transparent: material.transparent ?? false,
          depthWrite: material.depthWrite ?? true,
        });
      }
    });
  }, [materialsToFade]);

  useEffect(() => {
    if (!group.current) return;

    if (!miniGunRef.current) {
      let found = null;
      group.current.traverse((obj) => {
        if (!found && typeof obj?.name === "string" && obj.name.toLowerCase() === "mini-gun") {
          found = obj;
        }
      });
      if (found) {
        miniGunRef.current = found;
      }
    }

    if (miniGunRef.current && !muzzleRef.current) {
      let muzzle = null;
      miniGunRef.current.traverse((obj) => {
        if (muzzle || !obj?.name) return;
        const name = obj.name.toLowerCase();
        if (name.includes("muzzle") || name.includes("barrel") || name.includes("nozzle") || name.includes("tip")) {
          muzzle = obj;
        }
      });
      muzzleRef.current = muzzle || miniGunRef.current;
    }

    if (!miniGunRef.current) {
      muzzleRef.current = null;
    }

    if (miniGunRef.current) {
      miniGunRef.current.visible = Boolean(hasHeavyMachineGun);
    }
  }, [hasHeavyMachineGun, clonedScene]);

  const applyOpacity = useCallback(
    (value) => {
      materialsToFade.forEach((material) => {
        if (!material) return;
        const defaults = materialDefaultsRef.current.get(material);
        const nearOpaque = value >= 0.999;

        if (nearOpaque && defaults) {
          if ("opacity" in material) {
            material.opacity = defaults.opacity;
          }
          material.transparent = defaults.transparent;
          material.depthWrite = defaults.depthWrite;
        } else {
          if ("opacity" in material) {
            material.opacity = value;
          }
          material.transparent = true;
          material.depthWrite = false;
        }

        material.needsUpdate = true;
      });
      if (group.current) {
        group.current.visible = value > 0.02;
      }
      if (trailRef.current) {
        trailRef.current.visible = value > 0.05;
      }
    },
    [materialsToFade]
  );

  const applyDamageTint = useCallback(
    (active) => {
      materialsToFade.forEach((material) => {
        if (!material || !("color" in material) || !(material.color instanceof THREE.Color)) {
          return;
        }

        if (!damageTintDefaultsRef.current.has(material)) {
          damageTintDefaultsRef.current.set(material, {
            color: material.color.clone(),
            emissive:
              "emissive" in material && material.emissive instanceof THREE.Color
                ? material.emissive.clone()
                : null,
            emissiveIntensity:
              "emissiveIntensity" in material && typeof material.emissiveIntensity === "number"
                ? material.emissiveIntensity
                : null,
          });
        }

        const defaults = damageTintDefaultsRef.current.get(material);

        if (active) {
          material.color.copy(damageTintColor);

          if ("emissive" in material && material.emissive instanceof THREE.Color) {
            material.emissive.copy(damageTintColor).multiplyScalar(0.35);
          }

          if ("emissiveIntensity" in material && typeof material.emissiveIntensity === "number") {
            const baseIntensity =
              defaults && defaults.emissiveIntensity !== null && defaults.emissiveIntensity !== undefined
                ? defaults.emissiveIntensity
                : material.emissiveIntensity ?? 0;
            material.emissiveIntensity = Math.max(baseIntensity, 0.85);
          }
        } else if (defaults) {
          material.color.copy(defaults.color);

          if ("emissive" in material && material.emissive instanceof THREE.Color) {
            if (defaults.emissive) {
              material.emissive.copy(defaults.emissive);
            } else {
              material.emissive.setRGB(0, 0, 0);
            }
          }

          if (
            "emissiveIntensity" in material &&
            typeof material.emissiveIntensity === "number" &&
            defaults.emissiveIntensity !== null &&
            defaults.emissiveIntensity !== undefined
          ) {
            material.emissiveIntensity = defaults.emissiveIntensity;
          }
        }

        material.needsUpdate = true;
      });
    },
    [materialsToFade, damageTintColor]
  );

  useEffect(() => {
    applyOpacity(currentOpacityRef.current);
  }, [applyOpacity]);

  useEffect(() => {
    return () => {
      if (damageTintTimeoutRef.current !== null && typeof window !== "undefined") {
        window.clearTimeout(damageTintTimeoutRef.current);
      }
      applyDamageTint(false);
    };
  }, [applyDamageTint]);

  /**
   * Character animations setup
   */
  const curAnimation = useGame((state) => state.curAnimation);
  const resetAnimation = useGame((state) => state.reset);
  const initializeAnimationSet = useGame(
    (state) => state.initializeAnimationSet
  );
  const updateAnimationSet = useGame((state) => state.updateAnimationSet);
  const triggerDamageAnimation = useGame((state) => state.damage);
  const damageSequence = usePlayerHealth((state) => state.damageSequence);
  const setMovementLocked = useInteractionStore((state) => state.setMovementLocked);
  const setPlayerFrozenState = useInteractionStore((state) => state.setPlayerFrozenState);

  // Rename your character animations here
  const danceFloorClipName = useMemo(() => {
    const clip = extendedAnimations?.find((item) => item.name === DANCE_FLOOR_ACTION);
    return clip ? clip.name : null;
  }, [extendedAnimations]);

  const animationSet = useMemo(() => ({
    idle: "Idle",
    walk: "Walk",
    run: "Run",
    jump: "Jump_Start",
    jumpIdle: "Jump_Idle",
    jumpLand: "Jump_Land",
    fall: "Climbing", // This is for falling from high sky
    action1: "Wave",
    action2: "Dance",
    action3: "Cheer",
    action4: "Attack(1h)",
    danceFloor: danceFloorClipName ?? undefined,
    damage: damageClipName ?? undefined,
  }), [danceFloorClipName, damageClipName]);
  const defaultMovementClipsRef = useRef({ idle: animationSet.idle, walk: animationSet.walk });

  useEffect(() => {
    defaultMovementClipsRef.current = { idle: animationSet.idle, walk: animationSet.walk };
  }, [animationSet]);

  const gunAnimationOverrideAppliedRef = useRef(false);

  useEffect(() => {
    if (!gunMode) {
      if (gunAnimationOverrideAppliedRef.current) {
        updateAnimationSet({
          idle: defaultMovementClipsRef.current.idle,
          walk: defaultMovementClipsRef.current.walk,
        });

        const { curAnimation } = useGame.getState();
        if (curAnimation === gunIdleClipName) {
          useGame.setState({ curAnimation: defaultMovementClipsRef.current.idle });
        } else if (curAnimation === gunWalkClipName) {
          useGame.setState({ curAnimation: defaultMovementClipsRef.current.walk });
        }

        gunAnimationOverrideAppliedRef.current = false;
      }
      return;
    }

    if (!gunIdleClipName || !gunWalkClipName) {
      return;
    }

    updateAnimationSet({ idle: gunIdleClipName, walk: gunWalkClipName });

    const { curAnimation } = useGame.getState();
    if (curAnimation === defaultMovementClipsRef.current.idle) {
      useGame.setState({ curAnimation: gunIdleClipName });
    } else if (curAnimation === defaultMovementClipsRef.current.walk) {
      useGame.setState({ curAnimation: gunWalkClipName });
    }

    gunAnimationOverrideAppliedRef.current = true;
  }, [gunMode, gunIdleClipName, gunWalkClipName, updateAnimationSet]);

  useEffect(() => {
    // Initialize animation set
    initializeAnimationSet(animationSet);
  }, [animationSet, initializeAnimationSet]);

  useEffect(() => {
    if (damageSequence === 0) {
      return undefined;
    }

    triggerDamageAnimation();
    applyDamageTint(true);
    setMovementLocked(true);
    setPlayerFrozenState(true);

    if (damageTintTimeoutRef.current !== null && typeof window !== "undefined") {
      window.clearTimeout(damageTintTimeoutRef.current);
      damageTintTimeoutRef.current = null;
    }

    if (typeof window === "undefined") {
      applyDamageTint(false);
      return undefined;
    }

    const timeoutId = window.setTimeout(() => {
      applyDamageTint(false);
      damageTintTimeoutRef.current = null;
      setMovementLocked(false);
      setPlayerFrozenState(false);
    }, DAMAGE_FLASH_DURATION_MS);

    damageTintTimeoutRef.current = timeoutId;

    return () => {
      window.clearTimeout(timeoutId);
      damageTintTimeoutRef.current = null;
      setMovementLocked(false);
      setPlayerFrozenState(false);
    };
  }, [damageSequence, triggerDamageAnimation, applyDamageTint]);

  const clearPickupFallback = useCallback(() => {
    if (pickupFallbackTimeoutRef.current !== null) {
      clearTimeout(pickupFallbackTimeoutRef.current);
      pickupFallbackTimeoutRef.current = null;
    }
    pickupActiveActionsRef.current.clear();
  }, []);

  const startHeavyMachineGunSequence = useCallback(() => {
    if (!group.current) return;

    shootingPointerHeldRef.current = false;
    setIsShooting(false);
    stopShootingSequence(true);

    pickupActiveActionsRef.current.clear();
    pickupMixerCleanupRef.current.forEach(({ mixer, handler }) => {
      mixer.removeEventListener("finished", handler);
    });
    pickupMixerCleanupRef.current = [];
    clearPickupFallback();

    setMovementLocked(true);
    setPlayerFrozenState(true);

    const orientationState = orientationStateRef.current;
    orientationState.pendingReturn = false;
    orientationState.onComplete = null;
    orientationState.originalQuat.copy(group.current.quaternion);
    orientationState.initialQuat.copy(group.current.quaternion);

    if (camera) {
      group.current.getWorldPosition(orientationWorldPos);
      orientationTargetDir.subVectors(camera.position, orientationWorldPos);
      orientationTargetDir.y = 0;

      if (orientationTargetDir.lengthSq() > 1e-6) {
        orientationTargetDir.normalize();
        group.current.getWorldDirection(orientationCurrentForward);
        orientationCurrentForward.y = 0;
        if (orientationCurrentForward.lengthSq() < 1e-6) {
          orientationCurrentForward.set(0, 0, 1);
        } else {
          orientationCurrentForward.normalize();
        }

        const currentYaw = Math.atan2(orientationCurrentForward.x, orientationCurrentForward.z);
        const targetYaw = Math.atan2(orientationTargetDir.x, orientationTargetDir.z);
        let deltaYaw = targetYaw - currentYaw;
        deltaYaw = ((deltaYaw + Math.PI) % (Math.PI * 2)) - Math.PI;

        orientationYawQuat.setFromAxisAngle(orientationUp, deltaYaw);
        orientationState.targetQuat
          .copy(group.current.quaternion)
          .multiply(orientationYawQuat);
        orientationState.startTime =
          typeof performance !== "undefined" ? performance.now() : Date.now();
        orientationState.duration = 0.2;
        orientationState.phase = "toCamera";
      } else {
        orientationState.phase = "idle";
      }
    } else {
      orientationState.phase = "idle";
    }

    const actionList = [];
    if (pickupAnimationNames.primary) {
      const action = actions?.[pickupAnimationNames.primary];
      if (action) actionList.push(action);
    }
    if (pickupAnimationNames.secondary) {
      const action = actions?.[pickupAnimationNames.secondary];
      if (action && !actionList.includes(action)) actionList.push(action);
    }
    if (pickupAnimationNames.tertiary) {
      const action = actions?.[pickupAnimationNames.tertiary];
      if (action && !actionList.includes(action)) actionList.push(action);
    }

    if (actions) {
      Object.values(actions).forEach((action) => {
        if (!action || actionList.includes(action)) return;
        if (typeof action.isRunning === "function" ? action.isRunning() : action.isRunning) {
          action.fadeOut?.(0.05);
        }
        action.stop?.();
      });
    }

    const unlock = () => {
      clearPickupFallback();
      restoreOrientation();
      setMovementLocked(false);
      setPlayerFrozenState(false);
      pickupActiveActionsRef.current.clear();
    };

    if (actionList.length === 0) {
      if (orientationState.phase === "hold") {
        orientationState.initialQuat.copy(group.current.quaternion);
        orientationState.targetQuat.copy(orientationState.originalQuat);
        orientationState.startTime =
          typeof performance !== "undefined" ? performance.now() : Date.now();
        orientationState.duration = 0.2;
        orientationState.phase = "return";
        orientationState.onComplete = unlock;
        orientationState.pendingReturn = false;
      } else if (orientationState.phase === "toCamera") {
        orientationState.pendingReturn = true;
        orientationState.duration = 0.2;
        orientationState.onComplete = unlock;
      } else {
        unlock();
      }
      return;
    }

    const pending = new Set(actionList);
    const mixerEntries = new Set();

    const handleFinished = (event) => {
      if (!pending.has(event.action)) return;
      pending.delete(event.action);
      pickupActiveActionsRef.current.delete(event.action);

      if (pending.size === 0) {
        mixerEntries.forEach((mixer) => mixer.removeEventListener("finished", handleFinished));
        pickupMixerCleanupRef.current = [];

        const orient = orientationStateRef.current;
        const now = typeof performance !== "undefined" ? performance.now() : Date.now();

        if (orient.phase === "hold") {
          orient.pendingReturn = false;
          orient.initialQuat.copy(group.current.quaternion);
          orient.targetQuat.copy(orient.originalQuat);
          orient.startTime = now;
          orient.duration = 0.2;
          orient.phase = "return";
          orient.onComplete = unlock;
        } else if (orient.phase === "toCamera") {
          orient.pendingReturn = true;
          orient.duration = 0.2;
          orient.onComplete = unlock;
        } else {
          unlock();
        }
      }
    };

    actionList.forEach((action) => {
      if (action) {
        pickupActiveActionsRef.current.add(action);
      }
      const mixer = action?._mixer ?? null;
      if (mixer && !mixerEntries.has(mixer)) {
        mixerEntries.add(mixer);
        mixer.addEventListener("finished", handleFinished);
      }
      action.reset().setLoop(THREE.LoopOnce, 1).fadeIn(0.1).play();
      action.clampWhenFinished = true;
    });

    pickupMixerCleanupRef.current = Array.from(mixerEntries).map((mixer) => ({
      mixer,
      handler: handleFinished,
    }));

    if (typeof window !== "undefined") {
      const maxDuration = actionList.reduce((max, action) => {
        const clipDuration = action?.getClip?.()?.duration ?? action?._clip?.duration ?? 0;
        return Math.max(max, clipDuration);
      }, 0);
      const fallbackDelay = Math.max(1000, (maxDuration || 1.5) * 1000 + 400);
      pickupFallbackTimeoutRef.current = window.setTimeout(unlock, fallbackDelay);
    }
  }, [
  actions,
    camera,
    clearPickupFallback,
  setIsShooting,
  stopShootingSequence,
    restoreOrientation,
    orientationCurrentForward,
    orientationTargetDir,
    orientationUp,
    orientationWorldPos,
    orientationYawQuat,
    pickupAnimationNames,
    setMovementLocked,
    setPlayerFrozenState,
  ]);

  useEffect(() => {
    if (hasHeavyMachineGun && !previousHeavyMachineGunRef.current) {
      startHeavyMachineGunSequence();
    }
    previousHeavyMachineGunRef.current = hasHeavyMachineGun;
  }, [hasHeavyMachineGun, startHeavyMachineGunSequence]);

  useEffect(() => {
    if (!hasHeavyMachineGun) {
      stopGunfireEffects(true);
    }
  }, [hasHeavyMachineGun, stopGunfireEffects]);

  useEffect(() => {
    if (!gunMode) {
      stopGunfireEffects(true);
    }
  }, [gunMode, stopGunfireEffects]);

  useEffect(
    () => () => {
      pickupMixerCleanupRef.current.forEach(({ mixer, handler }) => {
        mixer.removeEventListener("finished", handler);
      });
      pickupMixerCleanupRef.current = [];
      clearPickupFallback();
    },
    [clearPickupFallback, restoreOrientation]
  );

  useEffect(
    () => () => {
      stopGunfireEffects(true);
    },
    [stopGunfireEffects]
  );

  useEffect(() => {
    group.current.traverse((obj) => {
      // Prepare both hands bone object
      if (obj instanceof THREE.Bone) {
        if (obj.name === "handSlotRight") rightHand = obj;
        if (obj.name === "handSlotLeft") leftHand = obj;
      }
    });
  });

  useFrame(() => {
    const orientationState = orientationStateRef.current;
    if (!group.current) return;
    if (orientationState.phase === "idle") return;

    const now = typeof performance !== "undefined" ? performance.now() : Date.now();
    const elapsed = (now - orientationState.startTime) / 1000;
    const duration = orientationState.duration > 0 ? orientationState.duration : 0.0001;
    const t = Math.min(1, elapsed / duration);

    orientationTempQuat
      .copy(orientationState.initialQuat)
      .slerp(orientationState.targetQuat, t);
    group.current.quaternion.copy(orientationTempQuat);

    if (t >= 1) {
      if (orientationState.phase === "toCamera") {
        orientationState.phase = "hold";
        group.current.quaternion.copy(orientationState.targetQuat);
        if (orientationState.pendingReturn) {
          orientationState.pendingReturn = false;
          orientationState.initialQuat.copy(orientationState.targetQuat);
          orientationState.targetQuat.copy(orientationState.originalQuat);
          orientationState.startTime =
            typeof performance !== "undefined" ? performance.now() : Date.now();
          orientationState.duration = 0.2;
          orientationState.phase = "return";
        }
      } else if (orientationState.phase === "return") {
        orientationState.phase = "idle";
        group.current.quaternion.copy(orientationState.targetQuat);
        const completion = orientationState.onComplete;
        orientationState.onComplete = null;
        orientationState.pendingReturn = false;
        if (typeof completion === "function") {
          completion();
        }
      }
    }
  });

  useFrame(() => {
    if (curAnimation === animationSet.action4) {
      if (rightHand) {
        rightHand.getWorldPosition(rightHandPos);
        group.current.getWorldPosition(bodyPos);
        group.current.getWorldQuaternion(bodyRot);
      }

      // Apply hands position to hand colliders
      if (rightHandColliderRef.current) {
        // check if parent group autobalance is on or off
        if (group.current.parent.quaternion.y === 0 && group.current.parent.quaternion.w === 1) {
          rightHandRef.current.position.copy(rightHandPos).sub(bodyPos).applyQuaternion(bodyRot.conjugate());
        } else {
          rightHandRef.current.position.copy(rightHandPos).sub(bodyPos);
        }
        rightHandColliderRef.current.setTranslationWrtParent(
          rightHandRef.current.position
        );
      }
    }
  });

  useFrame((state, delta) => {
    const instanced = bulletInstancedMeshRef.current;
    if (instanced) {
      const clampedDelta = Math.min(delta, 0.1);
      const advance = GUN_BULLET_SPEED * clampedDelta;
      let needsUpdate = false;
      let hasActive = false;

      bulletPoolRef.current.forEach((bullet) => {
        if (!bullet.active) return;
        hasActive = true;
        bullet.distance += advance;
        if (bullet.distance >= GUN_BULLET_RANGE) {
          bullet.active = false;
          instanced.setMatrixAt(bullet.index, hiddenMatrix);
          needsUpdate = true;
          return;
        }

        bullet.position.addScaledVector(bullet.direction, advance);
        bulletTempMatrix.compose(bullet.position, bullet.quaternion, bulletScale);
        instanced.setMatrixAt(bullet.index, bulletTempMatrix);
        needsUpdate = true;
      });

      if (needsUpdate) {
        instanced.instanceMatrix.needsUpdate = true;
      }

      instanced.visible = hasActive;
    }

    const flash = muzzleFlashRef.current;
    if (flash && flash.visible) {
      flash.lookAt(state.camera.position);
    }
  });

  useFrame((_, delta) => {
    const current = currentOpacityRef.current;
    const step = Math.min(delta / fadeDuration, 1);
    const next = THREE.MathUtils.lerp(current, targetOpacity, step);
    if (Math.abs(next - current) > 1e-3) {
      currentOpacityRef.current = next;
      applyOpacity(next);
    } else if (Math.abs(targetOpacity - current) > 1e-3) {
      currentOpacityRef.current = targetOpacity;
      applyOpacity(targetOpacity);
    }
  });

  useEffect(() => {
    if (typeof window === "undefined") {
      return undefined;
    }

    const handlePointerDown = (event) => {
      if (event.button !== 0) return;
      shootingPointerHeldRef.current = true;
      if (!gunMode) {
        setIsShooting(false);
        return;
      }
      setIsShooting(true);
      emitEvent("shooting", { phase: "start" });
      startShootingSequence();
    };

    const handlePointerUp = (event) => {
      if (event.button !== 0) return;
      shootingPointerHeldRef.current = false;
      setIsShooting(false);
      emitEvent("shooting", { phase: "stop" });
      stopShootingSequence(false);
    };

    window.addEventListener("pointerdown", handlePointerDown);
    window.addEventListener("pointerup", handlePointerUp);

    return () => {
      window.removeEventListener("pointerdown", handlePointerDown);
      window.removeEventListener("pointerup", handlePointerUp);
      stopShootingSequence(true);
      emitEvent("shooting", { phase: "stop" });
      setIsShooting(false);
      shootingPointerHeldRef.current = false;
    };
  }, [gunMode, setIsShooting, startShootingSequence, stopShootingSequence]);

  useEffect(() => {
    if (!gunMode) {
      shootingPointerHeldRef.current = false;
      setIsShooting(false);
      stopShootingSequence(true);
      emitEvent("shooting", { phase: "stop" });
    }
  }, [gunMode, setIsShooting, stopShootingSequence]);

  useEffect(() => {
    const targetActionName = curAnimation ? curAnimation : animationSet.jumpIdle;
    const action = actions[targetActionName];
    if (!action) return undefined;
    const activeGunWalk = gunMode && targetActionName === gunWalkClipName;

    if (gunWalkStopTimeoutRef.current !== null && activeGunWalk) {
      clearTimeout(gunWalkStopTimeoutRef.current);
      gunWalkStopTimeoutRef.current = null;
    }

    const protectedActions = new Set();
    shootingLoopActionsRef.current.forEach((loopAction) => {
      if (loopAction) protectedActions.add(loopAction);
    });
    if (shootingStartActionRef.current?.action) {
      protectedActions.add(shootingStartActionRef.current.action);
    }
    if (shootingIdleActionRef.current) {
      protectedActions.add(shootingIdleActionRef.current);
    }
    pickupActiveActionsRef.current.forEach((pickupAction) => {
      if (pickupAction) protectedActions.add(pickupAction);
    });

    if (activeGunWalk && actions) {
      Object.values(actions).forEach((otherAction) => {
        if (!otherAction || otherAction === action || protectedActions.has(otherAction)) {
          return;
        }
        if (otherAction.isRunning?.()) {
          otherAction.fadeOut?.(gunTransitionFade);
        } else {
          otherAction.stop?.();
        }
        otherAction.enabled = true;
      });
      action.reset().fadeIn?.(gunTransitionFade).setLoop(THREE.LoopRepeat, Infinity).play();
      action.clampWhenFinished = false;
    }

    if (curAnimation === animationSet.danceFloor) {
      action
        .reset()
        .fadeIn(0.2)
        .setLoop(THREE.LoopRepeat, Infinity)
        .play();
      action.clampWhenFinished = false;
    }
    // For jump and jump land animation, only play once and clamp when finish
    else if (
      curAnimation === animationSet.jump ||
      curAnimation === animationSet.jumpLand ||
      curAnimation === animationSet.action1 ||
      curAnimation === animationSet.action2 ||
      curAnimation === animationSet.action3 ||
      curAnimation === animationSet.action4 ||
      curAnimation === animationSet.damage
    ) {
      action
        .reset()
        .fadeIn(0.2)
        .setLoop(THREE.LoopOnce, undefined)
        .play();
      action.clampWhenFinished = true;
    } else {
      if (!activeGunWalk) {
        action.reset().fadeIn(0.2).play();
      }
    }

    // When any action is clamp and finished reset animation
    action._mixer.addEventListener("finished", () => resetAnimation());

    return () => {
      // Fade out previous action
      if (activeGunWalk) {
        action.fadeOut?.(gunTransitionFade);
        const stopDelay = gunTransitionFade > 0 ? gunTransitionFade * 1000 : 0;
        if (stopDelay > 0 && typeof window !== "undefined") {
          const timeoutId = window.setTimeout(() => {
            action.stop?.();
            gunWalkStopTimeoutRef.current = null;
          }, stopDelay);
          gunWalkStopTimeoutRef.current = timeoutId;
        } else {
          action.stop?.();
        }
      } else {
        action.fadeOut(0.2);
      }

      // Clean up mixer listener, and empty the _listeners array
      action._mixer.removeEventListener("finished", () =>
        resetAnimation()
      );
      action._mixer._listeners = [];

      // Move hand collider back to initial position after action
      if (curAnimation === animationSet.action4) {
        if (rightHandColliderRef.current) {
          rightHandColliderRef.current.setTranslationWrtParent(vec3({ x: 0, y: 0, z: 0 }))
        }
      }
    };
  }, [actions, animationSet, curAnimation, resetAnimation, gunMode, gunWalkClipName, gunTransitionFade]);

  return (
    <>
      {r3fScene && createPortal(
        <group>
          <instancedMesh ref={bulletInstancedMeshRef} args={[null, null, GUN_BULLET_CAPACITY]} frustumCulled={false}>
            <cylinderGeometry args={[BULLET_RADIUS, BULLET_RADIUS, BULLET_LENGTH, 12]} />
            <meshStandardMaterial
              color={BULLET_COLOR}
              emissive={BULLET_EMISSIVE}
              emissiveIntensity={BULLET_EMISSIVE_INTENSITY}
              metalness={0.1}
              roughness={0.35}
              toneMapped={false}
            />
          </instancedMesh>
          <mesh ref={muzzleFlashRef} visible={false} renderOrder={2}>
            <planeGeometry args={[0.6, 0.6]} />
            <meshBasicMaterial
              ref={muzzleFlashMaterialRef}
              color="#fff1b5"
              transparent
              opacity={0}
              blending={THREE.AdditiveBlending}
              side={THREE.DoubleSide}
              depthWrite={false}
            />
          </mesh>
        </group>,
        r3fScene
      )}
      <Suspense fallback={<capsuleGeometry args={[0.3, 0.7]} />}>
      {/* Default capsule modle */}
      {/* <mesh castShadow>
        <capsuleGeometry args={[0.3, 0.7]} />
        <meshStandardMaterial color="mediumpurple" />
      </mesh>
      <mesh castShadow position={[0, 0.2, 0.2]}>
        <boxGeometry args={[0.5, 0.2, 0.3]} />
        <meshStandardMaterial color="mediumpurple" />
      </mesh> */}

      {/* Replace yours model here */}
      {/* Head collider */}
      <BallCollider args={[0.5]} position={[0, 0.45, 0]} />
      {/* Right hand collider */}
      <group ref={rightHandRef} />
      <BallCollider
        args={[0.1]}
        ref={rightHandColliderRef}
        onCollisionEnter={(e) => {
          if (curAnimation === animationSet.action4) {
            // Play punch effect
            setPunchEffectProp((prev) => ({
              ...prev,
              visible: true,
              play: true,
            }));
          }
        }}
      />

      {/* Left hand collider */}
      <group ref={leftHandRef} />
      <BallCollider args={[0.1]} ref={leftHandColliderRef} />
      {/* Character model */}
      <group
        ref={group}
        {...props}
        dispose={null}
      >
  <group name="Scene" scale={0.8} position={[0, -0.86, 0]}>
          <group name="KayKit_Animated_Character">
            {nodes?.outline && (
              <skinnedMesh
                name="outline"
                geometry={nodes.outline.geometry}
                material={nodes.outline.material}
                skeleton={nodes.outline.skeleton}
              />
            )}
            {bodyMeshes.map((mesh) => (
              <skinnedMesh
                key={mesh.uuid}
                name={mesh.name}
                geometry={mesh.geometry}
                material={mesh.material}
                skeleton={mesh.skeleton}
                receiveShadow
                castShadow
              />
            ))}
            {nodes?.Body && (
              <Trail
                ref={trailRef}
                width={2}
                color={trailColor}
                length={5}
                attenuation={(width) => width}
              >
                <primitive object={nodes.Body} />
              </Trail>
            )}
          </group>
        </group>
        <SpriteAnimator
          visible={punchEffectProps.visible}
          scale={punchEffectProps.scale}
          position={punchEffectProps.position}
          startFrame={punchEffectProps.startFrame}
          loop={true}
          onLoopEnd={() => {
            setPunchEffectProp((prev) => ({
              ...prev,
              visible: false,
              play: false,
            }));
          }}
          play={punchEffectProps.play}
          numberOfFrames={7}
          alphaTest={0.01}
          // textureImageURL={"./punchEffect.png"}
          asSprite={true}
          spriteDataset={spriteObj}
        />
      </group>
      </Suspense>
    </>
  );
}

// Change the character src to yours
useGLTF.preload("/personaje.glb");
